import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { createIndexedDBStorage } from '@creanizer/utils'
import type { SavedRelationship, RelationshipInput, RelationshipResult } from '@creanizer/types'
import { migrateRelationshipData } from '../utils/migrateRelationshipData'

interface RelationshipStore {
  savedRelationships: SavedRelationship[]
  addRelationship: (data: { title: string; input: RelationshipInput; result: RelationshipResult }) => void
  updateRelationship: (id: string, updates: Partial<SavedRelationship>) => void
  deleteRelationship: (id: string) => void
  exportAsText: () => void
  exportAsJSON: () => void
  importFromJSON: (jsonString: string) => void
}

export const useRelationshipStore = create<RelationshipStore>()(
  persist(
    (set, get) => ({
      savedRelationships: [],

      addRelationship: (data) => {
        const currentRelationships = get().savedRelationships
        
        if (currentRelationships.length >= 200) {
          alert('保存数が上限（200件）に達しています。不要な関係性を削除してください。')
          return
        }

        const newRelationship: SavedRelationship = {
          id: crypto.randomUUID(),
          title: data.title,
          input: data.input,
          result: data.result,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }

        set(state => ({
          savedRelationships: [newRelationship, ...state.savedRelationships]
        }))
      },

      updateRelationship: (id, updates) => {
        set(state => ({
          savedRelationships: state.savedRelationships.map(r =>
            r.id === id
              ? { ...r, ...updates, updatedAt: new Date().toISOString() }
              : r
          )
        }))
      },

      deleteRelationship: (id) => {
        set(state => ({
          savedRelationships: state.savedRelationships.filter(r => r.id !== id)
        }))
      },

      exportAsText: () => {
        const { savedRelationships } = get()
        if (savedRelationships.length === 0) {
          alert('保存された関係性がありません。')
          return
        }

        const content = formatRelationshipsForExport(savedRelationships)
        downloadTextFile(content, `saved_relationships_${Date.now()}.txt`)
      },

      exportAsJSON: () => {
        const { savedRelationships } = get()
        if (savedRelationships.length === 0) {
          alert('保存された関係性がありません。')
          return
        }

        const exportData = {
          version: '1.0.0',
          exportDate: new Date().toISOString(),
          dataType: 'relationships',
          data: savedRelationships
        }

        downloadJSONFile(exportData, `saved_relationships_${Date.now()}.json`)
      },

      importFromJSON: (jsonString: string) => {
        try {
          const importData = JSON.parse(jsonString)
          
          // バリデーション
          if (!importData.data && !Array.isArray(importData)) {
            throw new Error('無効なデータ形式です')
          }

          const newRelationships = (importData.data || importData) as SavedRelationship[]
          const currentRelationships = get().savedRelationships
          
          // 重複チェック（タイトルとキャラクターで判定）
          const uniqueRelationships = newRelationships.filter(newRel => {
            return !currentRelationships.some(existing => 
              existing.title === newRel.title && 
              existing.result.characters[0]?.name === newRel.result.characters[0]?.name &&
              existing.result.characters[1]?.name === newRel.result.characters[1]?.name
            )
          })
          
          // 最大保存数チェック
          const maxRelationships = 200
          const totalCount = currentRelationships.length + uniqueRelationships.length
          if (totalCount > maxRelationships) {
            const availableSlots = maxRelationships - currentRelationships.length
            if (availableSlots <= 0) {
              alert(`保存数が上限（${maxRelationships}件）に達しています。不要な関係性を削除してください。`)
              return
            }
            if (!confirm(`追加可能な件数は${availableSlots}件です。${uniqueRelationships.length}件中${availableSlots}件のみインポートしますか？`)) {
              return
            }
            uniqueRelationships.splice(availableSlots)
          }

          // 新しいIDで追加
          const importedRelationships = uniqueRelationships.map(relationship => ({
            ...relationship,
            id: crypto.randomUUID(),
            createdAt: relationship.createdAt || new Date().toISOString(),
            updatedAt: relationship.updatedAt || new Date().toISOString()
          }))
          
          const mergedRelationships = [...currentRelationships, ...importedRelationships]
          set({ savedRelationships: mergedRelationships })
          
          const skippedCount = newRelationships.length - uniqueRelationships.length
          alert(`${importedRelationships.length}件の関係性をインポートしました。${skippedCount > 0 ? `(${skippedCount}件の重複をスキップ)` : ''}`)
        } catch (error) {
          console.error('Import error:', error)
          alert('インポートに失敗しました。ファイル形式を確認してください。')
        }
      }
    }),
    {
      name: 'relationship-storage',
      version: 1,
      storage: createIndexedDBStorage('relationship-storage'),
      partialize: (state) => ({ savedRelationships: state.savedRelationships }),
      migrate: (persistedState: any, version: number) => {
        // バージョン0（初期状態）からバージョン1への移行
        if (version === 0) {
          // 古いデータ構造から新しいデータ構造への変換
          return {
            savedRelationships: persistedState.savedRelationships || []
          }
        }
        return persistedState
      }
    }
  )
)

// 関係性をテキスト形式にフォーマット
function formatRelationshipsForExport(relationships: SavedRelationship[]): string {
  const date = new Date().toLocaleString('ja-JP')
  
  let content = `【保存された人物関係一覧】
生成日時: ${date}
総数: ${relationships.length}件

`

  relationships.forEach((relationship, index) => {
    const { result } = relationship
    content += `\n【${index + 1}】 ${relationship.title}\n`
    content += '=' .repeat(50) + '\n\n'
    
    // 概要
    content += `概要:\n${result.summary}\n\n`
    
    // 登場人物（詳細版）
    content += `登場人物 (${result.characters.length}名):\n`
    result.characters.forEach(char => {
      content += `\n◆ ${char.name}`
      if (char.isMain) {
        content += ' （主人公）'
      }
      content += '\n'
      if (char.profile) {
        content += `  プロフィール: ${char.profile}\n`
      }
      if (char.personality) {
        content += `  性格: ${char.personality}\n`
      }
      if (char.background) {
        content += `  背景: ${char.background}\n`
      }
    })
    
    content += '\n'
    
    // 関係性
    content += `関係性 (${result.relationships.length}個):\n`
    result.relationships.forEach((rel, relIndex) => {
      content += `  ${relIndex + 1}. ${rel.source} → ${rel.target}\n`
      content += `     関係: ${rel.relationshipType || rel.sourceToTargetType || ''}\n`
      content += `     説明: ${rel.description}\n`
      content += '\n'
    })
    
  })

  return content
}

// テキストファイルをダウンロード
function downloadTextFile(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// JSONファイルをダウンロード
function downloadJSONFile(data: any, filename: string) {
  const jsonString = JSON.stringify(data, null, 2)
  const blob = new Blob([jsonString], { type: 'application/json;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// 初回起動時にDexieからデータ移行
;(async () => {
  const migratedData = await migrateRelationshipData()
  if (migratedData.length > 0) {
    const currentState = useRelationshipStore.getState()
    if (currentState.savedRelationships.length === 0) {
      currentState.savedRelationships = migratedData
      console.log('✅ Dexieからデータを移行しました')
    }
  }
})()